/**
 * Product Catalog - Marketing For Schools
 * 10 Signage & Floor Graphics Products
 */

import type { Product, DeliveryOption, ProductCategory } from '../types/commerce';

export const products: Product[] = [
  // ========================================
  // FLOOR GRAPHICS (3 products)
  // ========================================
  {
    id: 'floor-logo-sticker',
    slug: 'school-logo-floor-sticker',
    name: 'School Logo Floor Sticker',
    category: 'floor-graphics',
    description: 'Durable floor graphic featuring your school logo. Perfect for entrances, halls, and corridors.',
    longDescription: 'Transform your school floors with our premium anti-slip vinyl floor stickers. Featuring your school logo in vibrant colours, these durable graphics are perfect for creating a strong first impression in entrances, halls, and corridors. Weather-resistant and designed to withstand heavy foot traffic.',
    basePrice: 45,
    material: 'Anti-slip vinyl with protective laminate',
    images: [
      'https://placehold.co/600x450/3b82f6/fff?text=School+Logo+Floor+Sticker',
      'https://placehold.co/600x450/3b82f6/fff?text=Floor+Logo+Detail',
      'https://placehold.co/600x450/3b82f6/fff?text=Installation+View',
      'https://placehold.co/600x450/3b82f6/fff?text=In+Use'
    ],
    sizeOptions: [
      { id: 'floor-logo-60', label: '60cm Diameter', dimensions: '60cm ø', priceModifier: 0 },
      { id: 'floor-logo-80', label: '80cm Diameter', dimensions: '80cm ø', priceModifier: 20 },
      { id: 'floor-logo-100', label: '100cm Diameter', dimensions: '100cm ø', priceModifier: 40 }
    ],
    bulkDiscounts: [
      { minQuantity: 5, discountPercent: 10 },
      { minQuantity: 10, discountPercent: 20 }
    ],
    inStock: true,
    featured: true,
    specifications: {
      'Material': 'Anti-slip vinyl',
      'Finish': 'Matt protective laminate',
      'Installation': 'Self-adhesive backing',
      'Lifespan': '3-5 years indoor use',
      'Cleaning': 'Damp mop safe'
    }
  },
  {
    id: 'social-distance-markers',
    slug: 'social-distance-floor-markers',
    name: 'Social Distance Floor Markers',
    category: 'floor-graphics',
    description: 'Clear 2-metre distancing markers. Weather-resistant for indoor/outdoor use.',
    longDescription: 'High-visibility floor markers designed to help maintain social distancing in schools. Each marker clearly indicates the 2-metre safe distance with bold graphics and clear messaging. Suitable for both indoor and outdoor use with weather-resistant materials.',
    basePrice: 12,
    material: 'Anti-slip vinyl, weather-resistant',
    images: [
      'https://placehold.co/600x450/3b82f6/fff?text=Social+Distance+Markers',
      'https://placehold.co/600x450/3b82f6/fff?text=Floor+Markers+Detail',
      'https://placehold.co/600x450/3b82f6/fff?text=Outdoor+Use'
    ],
    sizeOptions: [
      { id: 'distance-30', label: 'Standard (30cm circles)', dimensions: '30cm ø', priceModifier: 0 }
    ],
    bulkDiscounts: [
      { minQuantity: 10, discountPercent: 15 },
      { minQuantity: 25, discountPercent: 25 }
    ],
    inStock: true,
    featured: false,
    specifications: {
      'Material': 'Anti-slip vinyl',
      'Diameter': '30cm',
      'Colours': 'Yellow/Black safety colours',
      'Use': 'Indoor & outdoor',
      'Adhesion': 'Permanent adhesive'
    }
  },
  {
    id: 'directional-arrows',
    slug: 'directional-arrow-floor-stickers',
    name: 'Directional Arrow Floor Stickers',
    category: 'floor-graphics',
    description: 'Guide students and visitors with clear directional arrows. Available in school colours.',
    longDescription: 'Professional wayfinding solution for busy school corridors and halls. Our directional arrow floor stickers help guide students and visitors efficiently while reducing congestion. Customisable in your school colours to maintain brand consistency throughout your building.',
    basePrice: 18,
    material: 'Anti-slip vinyl',
    images: [
      'https://placehold.co/600x450/3b82f6/fff?text=Directional+Arrow+Stickers',
      'https://placehold.co/600x450/3b82f6/fff?text=Arrow+Detail',
      'https://placehold.co/600x450/3b82f6/fff?text=Corridor+Wayfinding'
    ],
    sizeOptions: [
      { id: 'arrow-40', label: 'Small (40cm)', dimensions: '40cm', priceModifier: 0 },
      { id: 'arrow-60', label: 'Large (60cm)', dimensions: '60cm', priceModifier: 14 }
    ],
    bulkDiscounts: [
      { minQuantity: 6, discountPercent: 12 },
      { minQuantity: 12, discountPercent: 20 }
    ],
    inStock: true,
    featured: false,
    specifications: {
      'Material': 'Anti-slip vinyl',
      'Colours': 'Customisable to school colours',
      'Finish': 'Matt laminate',
      'Installation': 'Easy peel & stick',
      'Durability': 'Heavy traffic rated'
    }
  },

  // ========================================
  // WALL SIGNAGE (4 products)
  // ========================================
  {
    id: 'school-values-wall',
    slug: 'school-values-wall-display',
    name: 'School Values Wall Display',
    category: 'wall-signage',
    description: 'Showcase your school values with professionally designed wall signage.',
    longDescription: 'Make your school values visible and memorable with our premium wall displays. Professionally designed to incorporate your school branding, these displays create a lasting impression on students, staff, and visitors. Available in multiple sizes and premium materials for different budgets and locations.',
    basePrice: 65,
    material: 'Foamex board or premium canvas',
    images: [
      'https://placehold.co/600x450/f59e0b/fff?text=School+Values+Display',
      'https://placehold.co/600x450/f59e0b/fff?text=Values+Wall+Detail',
      'https://placehold.co/600x450/f59e0b/fff?text=Reception+Area',
      'https://placehold.co/600x450/f59e0b/fff?text=Values+Typography'
    ],
    sizeOptions: [
      { id: 'values-a2', label: 'A2 (420x594mm)', dimensions: '420x594mm', priceModifier: 0 },
      { id: 'values-a1', label: 'A1 (594x841mm)', dimensions: '594x841mm', priceModifier: 40 },
      { id: 'values-a0', label: 'A0 (841x1189mm)', dimensions: '841x1189mm', priceModifier: 80 }
    ],
    bulkDiscounts: [],
    inStock: true,
    featured: true,
    specifications: {
      'Material Options': 'Foamex board or canvas',
      'Mounting': 'Hanging fixings included',
      'Design': 'Custom to your school values',
      'Turnaround': '5-7 working days',
      'Finish': 'Matt or gloss available'
    }
  },
  {
    id: 'classroom-door-signs',
    slug: 'classroom-door-signs',
    name: 'Classroom Door Signs',
    category: 'wall-signage',
    description: 'Durable classroom identification signs. Customised with room name/number.',
    longDescription: 'Professional classroom door signs that help visitors and students navigate your school with ease. Each sign is customised with your chosen room name, number, and can include teacher name or subject. Rigid PVC construction ensures durability and maintains appearance for years.',
    basePrice: 22,
    material: 'Rigid PVC with rounded corners',
    images: [
      'https://placehold.co/600x450/f59e0b/fff?text=Classroom+Door+Signs',
      'https://placehold.co/600x450/f59e0b/fff?text=Door+Sign+Detail',
      'https://placehold.co/600x450/f59e0b/fff?text=Mounted+View'
    ],
    sizeOptions: [
      { id: 'door-a4', label: 'A4 Standard (297x210mm)', dimensions: '297x210mm', priceModifier: 0 }
    ],
    bulkDiscounts: [
      { minQuantity: 10, discountPercent: 15 },
      { minQuantity: 20, discountPercent: 25 }
    ],
    inStock: true,
    featured: false,
    specifications: {
      'Material': '3mm rigid PVC',
      'Corners': 'Rounded for safety',
      'Mounting': 'Pre-drilled holes or adhesive pads',
      'Customization': 'Room name, number, teacher name',
      'Colors': 'Match school branding'
    }
  },
  {
    id: 'welcome-banner-indoor',
    slug: 'welcome-banner-indoor',
    name: 'Welcome Banner (Indoor)',
    category: 'wall-signage',
    description: 'Create a welcoming entrance with a professional welcome banner.',
    longDescription: 'Make a stunning first impression with our premium indoor welcome banners. Perfect for reception areas, entrance halls, and assembly rooms. Full-colour printing on high-quality PVC ensures your school branding looks vibrant and professional. Easy to install with eyelets for hanging.',
    basePrice: 75,
    material: 'Premium PVC banner material',
    images: [
      'https://placehold.co/600x450/f59e0b/fff?text=Welcome+Banner',
      'https://placehold.co/600x450/f59e0b/fff?text=Reception+Display',
      'https://placehold.co/600x450/f59e0b/fff?text=Banner+Detail'
    ],
    sizeOptions: [
      { id: 'banner-2x1', label: '2m x 1m', dimensions: '2000x1000mm', priceModifier: 0 },
      { id: 'banner-3x1', label: '3m x 1m', dimensions: '3000x1000mm', priceModifier: 25 },
      { id: 'banner-4x1', label: '4m x 1m', dimensions: '4000x1000mm', priceModifier: 50 }
    ],
    bulkDiscounts: [],
    inStock: true,
    featured: false,
    specifications: {
      'Material': '440gsm PVC banner',
      'Finish': 'Matt or gloss',
      'Eyelets': 'Every 50cm for hanging',
      'Printing': 'Full-colour high resolution',
      'Use': 'Indoor only'
    }
  },
  {
    id: 'school-motto-wall-art',
    slug: 'school-motto-wall-art',
    name: 'School Motto Wall Art',
    category: 'wall-signage',
    description: 'Inspirational wall art featuring your school motto. Premium finish options.',
    longDescription: 'Inspire students daily with beautifully designed wall art featuring your school motto. Choose from canvas for a classic look or aluminium composite for modern sophistication. Each piece is custom-designed to incorporate your school branding, colours, and typography for a truly unique statement piece.',
    basePrice: 85,
    material: 'Canvas or aluminium composite',
    images: [
      'https://placehold.co/600x450/f59e0b/fff?text=School+Motto+Wall+Art',
      'https://placehold.co/600x450/f59e0b/fff?text=Typography+Detail',
      'https://placehold.co/600x450/f59e0b/fff?text=Canvas+Version',
      'https://placehold.co/600x450/f59e0b/fff?text=Aluminium+Version'
    ],
    sizeOptions: [
      { id: 'motto-a1', label: 'A1 (594x841mm)', dimensions: '594x841mm', priceModifier: 0 },
      { id: 'motto-a0', label: 'A0 (841x1189mm)', dimensions: '841x1189mm', priceModifier: 40 },
      { id: 'motto-custom', label: 'Custom Size', dimensions: 'Custom', priceModifier: 80 }
    ],
    bulkDiscounts: [],
    inStock: true,
    featured: true,
    specifications: {
      'Materials': 'Canvas or 3mm aluminium composite',
      'Canvas Mounting': 'Stretched on wooden frame',
      'Aluminium Mounting': 'Wall fixing kit included',
      'Design': 'Custom typography and layout',
      'Turnaround': '7-10 working days'
    }
  },

  // ========================================
  // OUTDOOR SIGNAGE (3 products)
  // ========================================
  {
    id: 'a-frame-sign',
    slug: 'a-frame-pavement-sign',
    name: 'A-Frame Pavement Sign',
    category: 'outdoor-signage',
    description: 'Double-sided outdoor sign for events, open days, and notices.',
    longDescription: 'Versatile outdoor advertising solution perfect for promoting open days, events, and important notices. Our A-frame pavement signs feature double-sided graphics for maximum visibility. Weather-resistant construction withstands UK weather while the weighted base prevents tipping in wind.',
    basePrice: 95,
    material: 'Weather-resistant metal frame with printed inserts',
    images: [
      'https://placehold.co/600x450/10b981/fff?text=A-Frame+Pavement+Sign',
      'https://placehold.co/600x450/10b981/fff?text=Double+Sided+View',
      'https://placehold.co/600x450/10b981/fff?text=Outdoor+Use'
    ],
    sizeOptions: [
      { id: 'aframe-a1', label: 'A1 Visible Area', dimensions: 'A1 per side', priceModifier: 0 }
    ],
    bulkDiscounts: [],
    inStock: true,
    featured: false,
    specifications: {
      'Frame': 'Powder-coated steel',
      'Base': 'Weighted for stability',
      'Inserts': 'Weatherproof printed panels',
      'Visibility': 'Double-sided',
      'Storage': 'Folds flat'
    }
  },
  {
    id: 'entrance-gate-banner',
    slug: 'entrance-gate-banner',
    name: 'Entrance Gate Banner',
    category: 'outdoor-signage',
    description: 'Large outdoor banner for school gates. Wind-resistant mesh material.',
    longDescription: 'Make a bold statement at your school entrance with our large-format gate banners. Designed specifically for outdoor use, the mesh PVC material allows wind to pass through, preventing damage in adverse weather. Perfect for showcasing achievements, promoting events, or displaying your school branding.',
    basePrice: 110,
    material: 'Heavy-duty PVC mesh',
    images: [
      'https://placehold.co/600x450/10b981/fff?text=Entrance+Gate+Banner',
      'https://placehold.co/600x450/10b981/fff?text=School+Gate+Display',
      'https://placehold.co/600x450/10b981/fff?text=Wind+Resistant+Mesh'
    ],
    sizeOptions: [
      { id: 'gate-3x1', label: '3m x 1m', dimensions: '3000x1000mm', priceModifier: 0 },
      { id: 'gate-4x1', label: '4m x 1m', dimensions: '4000x1000mm', priceModifier: 30 },
      { id: 'gate-5x1', label: '5m x 1m', dimensions: '5000x1000mm', priceModifier: 55 }
    ],
    bulkDiscounts: [],
    inStock: true,
    featured: false,
    specifications: {
      'Material': 'Mesh PVC (wind-resistant)',
      'Weight': '370gsm',
      'Eyelets': 'Reinforced every 50cm',
      'Printing': 'UV-resistant inks',
      'Warranty': '12 months outdoor use'
    }
  },
  {
    id: 'carpark-wayfinding',
    slug: 'car-park-wayfinding-signs',
    name: 'Car Park Wayfinding Signs',
    category: 'outdoor-signage',
    description: 'Clear directional signage for visitor parking. Includes mounting post.',
    longDescription: 'Help visitors find their way with professional car park directional signs. Each sign is made from durable aluminium composite that resists fading and weather damage. Supplied with a galvanized steel post and ground fixing kit for easy installation. Clear graphics and text ensure excellent visibility.',
    basePrice: 55,
    material: 'Aluminium composite on galvanized post',
    images: [
      'https://placehold.co/600x450/10b981/fff?text=Car+Park+Wayfinding',
      'https://placehold.co/600x450/10b981/fff?text=Directional+Sign',
      'https://placehold.co/600x450/10b981/fff?text=Mounted+on+Post'
    ],
    sizeOptions: [
      { id: 'carpark-a3', label: 'A3 on Post', dimensions: 'A3 (420x297mm)', priceModifier: 0 },
      { id: 'carpark-a2', label: 'A2 on Post', dimensions: 'A2 (594x420mm)', priceModifier: 30 }
    ],
    bulkDiscounts: [
      { minQuantity: 3, discountPercent: 10 },
      { minQuantity: 5, discountPercent: 18 }
    ],
    inStock: true,
    featured: false,
    specifications: {
      'Sign Material': '3mm aluminium composite',
      'Post': 'Galvanized steel 2.5m',
      'Mounting': 'Ground fixing kit included',
      'Visibility': 'Reflective options available',
      'Installation': 'Professional recommended'
    }
  }
];

// Delivery options
export const deliveryOptions: DeliveryOption[] = [
  {
    id: 'standard',
    name: 'Standard Delivery',
    description: '5-7 working days',
    price: 8.95,
    estimatedDays: '5-7 working days'
  },
  {
    id: 'express',
    name: 'Express Delivery',
    description: '2-3 working days',
    price: 14.95,
    estimatedDays: '2-3 working days'
  },
  {
    id: 'collection',
    name: 'Click & Collect',
    description: 'Collect from Margate, Kent',
    price: 0,
    estimatedDays: 'Ready in 5-7 days'
  }
];

// Helper functions
export function getProductBySlug(slug: string): Product | undefined {
  return products.find(p => p.slug === slug);
}

export function getProductsByCategory(category: ProductCategory): Product[] {
  return products.filter(p => p.category === category);
}

export function getFeaturedProducts(): Product[] {
  return products.filter(p => p.featured);
}

export function calculateBulkDiscount(product: Product, quantity: number): number {
  const applicableDiscounts = product.bulkDiscounts
    .filter(d => quantity >= d.minQuantity)
    .sort((a, b) => b.discountPercent - a.discountPercent);

  return applicableDiscounts.length > 0 ? applicableDiscounts[0].discountPercent : 0;
}
